import {Box, Checkbox, Group, PasswordInput, Select, Text, TextInput} from '@mantine/core';
import React, {useState} from 'react';

import {MCP_PRODUCT_NAME} from 'llm/NamingConstants';
import notifications from 'notifications';
import {ControlledAccordion, preventAccordionToggleProps} from 'ui-library/Accordion';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT, GRAY_SEMIDARK} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useFormInput from 'ui-library/hooks/useFormInput';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import {postJSONPromise} from 'util/Net';
import {withLoadingScreen} from 'util/Promises';

interface AddMCPServerModalProps {
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

type TransportType = 'STREAMABLE_HTTP' | 'SSE';

const TRANSPORT_OPTIONS: Array<{value: TransportType; label: string}> = [
  {value: 'STREAMABLE_HTTP', label: 'Streamable HTTP'},
  {value: 'SSE', label: 'SSE'},
];

export default function AddMCPServerModal(props: AddMCPServerModalProps): JSX.Element {
  const nameInput = useFormInput('');
  const serverUrlInput = useFormInput('');
  const [transportType, setTransportType] = useState<TransportType>('STREAMABLE_HTTP');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Manual registration state
  const [useManualRegistration, setUseManualRegistration] = useState(false);
  const clientIdInput = useFormInput('');
  const clientSecretInput = useFormInput('');

  // OAuth provider overrides
  const [showOAuthOverrides, setShowOAuthOverrides] = useState(false);
  const [suppressResourceParam, setSuppressResourceParam] = useState(false);
  const authorizationUrlInput = useFormInput('');
  const tokenUrlInput = useFormInput('');

  // Construct the redirect URI from the current origin
  const redirectUri = `${window.location.origin}/1/api/llm/mcp/oauth/callback`;

  const isBaseValid = nameInput.value.trim() !== '' && serverUrlInput.value.trim() !== '';
  const isManualRegistrationValid =
    !useManualRegistration || (clientIdInput.value.trim() !== '' && clientSecretInput.value.trim() !== '');
  // OAuth endpoint URLs must be provided as a pair (both or neither)
  const authUrlTrimmed = authorizationUrlInput.value.trim();
  const tokenUrlTrimmed = tokenUrlInput.value.trim();
  const isOAuthEndpointsValid =
    !useManualRegistration || !showOAuthOverrides || (authUrlTrimmed === '') === (tokenUrlTrimmed === '');
  const isValid = isBaseValid && isManualRegistrationValid && isOAuthEndpointsValid;

  async function handleSubmit(): Promise<void> {
    try {
      setIsSubmitting(true);
      const data: Record<string, unknown> = {
        name: nameInput.value.trim(),
        serverUrl: serverUrlInput.value.trim(),
        transportType,
      };

      if (useManualRegistration) {
        data.clientId = clientIdInput.value.trim();
        data.clientSecret = clientSecretInput.value.trim();

        // Include oauthConfig if overrides section is enabled and any values are set
        if (showOAuthOverrides) {
          const oauthConfig: Record<string, unknown> = {};
          if (suppressResourceParam) {
            oauthConfig.suppressResourceParam = true;
          }
          if (authorizationUrlInput.value.trim() !== '') {
            oauthConfig.authorizationUrl = authorizationUrlInput.value.trim();
          }
          if (tokenUrlInput.value.trim() !== '') {
            oauthConfig.tokenUrl = tokenUrlInput.value.trim();
          }
          if (Object.keys(oauthConfig).length > 0) {
            data.oauthConfig = oauthConfig;
          }
        }
      }

      await withLoadingScreen(
        postJSONPromise({
          url: '/1/api/llm/mcp-server-configs',
          data,
        }),
        `Adding ${MCP_PRODUCT_NAME}...`
      );
      notifications.success(`${MCP_PRODUCT_NAME} added successfully`);
      props.onRequestSubmit();
    } finally {
      setIsSubmitting(false);
    }
  }

  const accordionTitle = (title: string, tooltip: string): JSX.Element => (
    <Group spacing={4} align="center" noWrap={true}>
      <Text size="md" weight={600}>
        {title}
      </Text>
      <Tooltip text={tooltip}>
        <span {...preventAccordionToggleProps()} style={{cursor: 'help', display: 'inline-flex'}}>
          <BenchlingIcon icon="info-circle" color={GRAY_SEMIDARK} />
        </span>
      </Tooltip>
    </Group>
  );

  return (
    <Modal
      title={`Add ${MCP_PRODUCT_NAME}`}
      onRequestSubmit={handleSubmit}
      onRequestClose={props.onRequestClose}
      buttons={[
        {text: 'Cancel', cancel: true},
        {
          text: 'Add',
          submit: true,
          style: ButtonStyle.PRIMARY,
          disabled: !isValid || isSubmitting,
        },
      ]}
    >
      <Text size="sm" color="dimmed" mb="md">
        Get details from MCP servers that your team hosts or from an external tool you trust to add an{' '}
        {MCP_PRODUCT_NAME}.
      </Text>
      <FormGroup>
        <TextInput
          label="Name"
          placeholder="My MCP Server"
          value={nameInput.value}
          onChange={nameInput.onChange}
          autoFocus={true}
          maxLength={255}
          withAsterisk={true}
        />
      </FormGroup>
      <FormGroup>
        <TextInput
          label="Server URL"
          placeholder="https://mcp.example.com/mcp"
          value={serverUrlInput.value}
          onChange={serverUrlInput.onChange}
          maxLength={2048}
          withAsterisk={true}
        />
      </FormGroup>
      <FormGroup>
        <Select
          label="Transport Type"
          placeholder="Pick transport"
          data={TRANSPORT_OPTIONS}
          value={transportType}
          onChange={(v) => {
            if (v === 'STREAMABLE_HTTP' || v === 'SSE') {
              setTransportType(v);
            }
          }}
          withAsterisk={true}
        />
      </FormGroup>

      {/* Advanced: Manual Registration */}
      <Box
        mt="md"
        p="md"
        sx={{
          backgroundColor: GRAY_LIGHT,
          borderRadius: 4,
        }}
      >
        <ControlledAccordion
          isOpen={useManualRegistration}
          setIsOpen={setUseManualRegistration}
          headerStyle="borderless"
          headerSpacing="none"
          header={accordionTitle(
            'Manual OAuth registration',
            'Use this if the MCP server requires you to manually register an OAuth application in their developer console.'
          )}
        >
          <Box pt="md">
            <FormGroup>
              <TextInput
                label="Redirect URI (click to copy)"
                value={redirectUri}
                readOnly={true}
                onClick={() => {
                  copyToClipboard(redirectUri, {
                    showToastOnSuccess: true,
                    toastText: 'Redirect URI copied to clipboard!',
                  });
                }}
                styles={{input: {cursor: 'copy'}}}
              />
              <Text size="xs" color="dimmed" mt={4}>
                Copy this URI and paste it into the MCP server&apos;s developer console when creating your
                OAuth application.
              </Text>
            </FormGroup>
            <FormGroup>
              <TextInput
                label="Client ID"
                placeholder="Enter the client ID from the MCP server"
                value={clientIdInput.value}
                onChange={clientIdInput.onChange}
                maxLength={2048}
                withAsterisk={true}
              />
            </FormGroup>
            <FormGroup>
              <PasswordInput
                label="Client Secret"
                placeholder="Enter the client secret from the MCP server"
                value={clientSecretInput.value}
                onChange={clientSecretInput.onChange}
                maxLength={2048}
                withAsterisk={true}
              />
            </FormGroup>

            {/* OAuth Provider Overrides */}
            <Box
              pt="md"
              mt="md"
              sx={{
                borderTop: `1px solid ${GRAY_LIGHT}`,
              }}
            >
              <ControlledAccordion
                isOpen={showOAuthOverrides}
                setIsOpen={setShowOAuthOverrides}
                headerStyle="borderless"
                headerSpacing="none"
                header={accordionTitle(
                  'OAuth provider overrides',
                  "For OAuth providers like Microsoft Entra that require non-standard endpoint URLs or don't support RFC 8707 resource parameters."
                )}
              >
                <Box pt={12}>
                  <Group spacing={4} align="center" mb="sm">
                    <Checkbox
                      label="Suppress resource parameter"
                      checked={suppressResourceParam}
                      onChange={(e) => {
                        setSuppressResourceParam(e.currentTarget.checked);
                      }}
                    />
                    <Tooltip text="Enable this if the OAuth provider rejects requests containing the RFC 8707 resource parameter.">
                      <span style={{cursor: 'help', display: 'inline-flex'}}>
                        <BenchlingIcon icon="info-circle" color={GRAY_SEMIDARK} />
                      </span>
                    </Tooltip>
                  </Group>
                  <FormGroup>
                    <TextInput
                      label="Authorization URL"
                      placeholder="https://login.microsoftonline.com/{tenant}/oauth2/v2.0/authorize"
                      value={authorizationUrlInput.value}
                      onChange={authorizationUrlInput.onChange}
                      maxLength={2048}
                    />
                  </FormGroup>
                  <FormGroup>
                    <TextInput
                      label="Token URL"
                      placeholder="https://login.microsoftonline.com/{tenant}/oauth2/v2.0/token"
                      value={tokenUrlInput.value}
                      onChange={tokenUrlInput.onChange}
                      maxLength={2048}
                    />
                  </FormGroup>
                  {(authUrlTrimmed === '') !== (tokenUrlTrimmed === '') && (
                    <Text size="xs" color="red" mt={0}>
                      Both Authorization URL and Token URL must be provided together.
                    </Text>
                  )}
                </Box>
              </ControlledAccordion>
            </Box>
          </Box>
        </ControlledAccordion>
      </Box>
    </Modal>
  );
}
